import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, throwError } from 'rxjs';
import { catchError, exhaustMap, map, switchMap } from 'rxjs/operators';
import { selectSubAccountId } from '../../account-info-header/reducers';
import {
  dateTypes,
  toFromAccountAffiliate,
  transactionDirection,
} from '../../shared/utilities/Constants';
import { StandingAuthDetailsActions } from '../../standing-authorizations/actions';
import { selectStandingAuthAchInfo, selectStandingAuthFormExtraPrompts } from '../../standing-authorizations/reducers';
import { TransactionDetailsActions } from '../actions';
import { CashieringDateRequest } from '../models/cashieringDate/CashieringDateRequest';
import { CashieringDateResponse } from '../models/cashieringDate/CashieringDateResponse';
import { ServiceError } from '../models/shared/ServiceError';
import {
  selectAchInfo,
  selectTransactionFormExtraPrompts
} from '../reducers';
import { CashieringDateService } from '../services/cashiering-date.service';
import { isAchExtraPrompts } from '../../standing-authorizations/models/standingAuthorizationForm';
import { concatLatestFrom } from '@ngrx/operators';

@Injectable()
export class CashieringDateEffects {
  constructor(
    private actions$: Actions,
    private cashieringDateService: CashieringDateService,
    private store: Store
  ) {}

  loadCashieringDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionDetailsActions.loadWithdrawalDate),
      concatLatestFrom(() => [
        this.store.select(selectSubAccountId),
        this.store.select(selectAchInfo),
        this.store.select(selectTransactionFormExtraPrompts),
      ]),
      exhaustMap(([action, subAccountId, achInfo, extraPrompts]) => {
        if (extraPrompts && isAchExtraPrompts(extraPrompts)) {
          let request = this.createCashieringDateRequest(
            extraPrompts.transferDirection,
            subAccountId,
            achInfo.toFromAccountNumber,
            achInfo.toFromAccount.abaNumber,
            action.completionDate
          );

          return this.cashieringDateService.getCashieringDate(request).pipe(
            map((cashieringDateResponse: CashieringDateResponse) => {
              return TransactionDetailsActions.loadWithdrawalDateSuccess(
                cashieringDateResponse
              );
            }),
            catchError((err: ServiceError) =>
              of(TransactionDetailsActions.loadWithdrawalDateFailure(err))
            )
          );
        }
        return throwError(
          'transaction type not supported for expected completion date'
        );
      })
    )
  );

  loadCashieringDateStandingAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StandingAuthDetailsActions.loadWithdrawalDate),
      concatLatestFrom(() => [
        this.store.select(selectSubAccountId),
        this.store.select(selectStandingAuthAchInfo),
        this.store.select(selectStandingAuthFormExtraPrompts),
      ]),
      exhaustMap(([action, subAccountId, achInfo, extraPrompts]) => {
        if (extraPrompts && isAchExtraPrompts(extraPrompts)) {
          let request = this.createCashieringDateRequest(
            extraPrompts.transferDirection,
            subAccountId,
            achInfo.toFromAccountNumber,
            achInfo.toFromAccount.abaNumber,
            action.completionDate
          );

          return this.cashieringDateService.getCashieringDate(request).pipe(
            map((cashieringDateResponse: CashieringDateResponse) => {
              return StandingAuthDetailsActions.loadWithdrawalDateSuccess(
                cashieringDateResponse
              );
            }),
            catchError((err: ServiceError) =>
              of(StandingAuthDetailsActions.loadWithdrawalDateFailure(err))
            )
          );
        }
        return throwError(
          'transaction type not supported for expected completion date'
        );
      })
    )
  );

  createCashieringDateRequest(
    direction: string,
    subAccountId: string,
    toFromAccountId: string,
    abaNumber: string,
    withdrawalDate: string
  ): CashieringDateRequest {
    if (direction === transactionDirection.in.toLowerCase()) {
      return {
        toAccount: subAccountId,
        fromAccount: toFromAccountId,
        toAccountAffiliate: toFromAccountAffiliate.schwab,
        fromAccountAffiliate: toFromAccountAffiliate.external,
        abaNumber: abaNumber,
        withdrawalDate: withdrawalDate,
        dateType: dateTypes.settlementDate
      };
    }
    return {
      fromAccount: subAccountId,
      toAccount: toFromAccountId,
      fromAccountAffiliate: toFromAccountAffiliate.schwab,
      toAccountAffiliate: toFromAccountAffiliate.external,
      abaNumber: abaNumber,
      withdrawalDate: withdrawalDate,
      dateType: dateTypes.settlementDate
    };
  }
}
